import React from "react";
import { RESOURCE_KEYS } from "utils/OrgPermissions";
import { hasMenuPermission } from "utils/AuthUtil";
import {
  SHARE_RECORDING_FORM_KEYS,
  DEFAULT_ACCESSIBILITY_VAL,
  SHARE_RECORDING_TEXTS,
} from "../ShareRecording.constant";

/**
 * Custom hook to manage ShareRecording state, computed attributes & handlers.
 */
export const useShareRecording = ({
  isAutoRecordable,
  mapToCourse,
  recordingAccessControl,
  showAutoPackage = false,
  form,
}) => {
  const [hasShareRecordingAccess, setHasShareRecordingAccess] =
    React.useState(false);
  const [showFaqModal, setShowFaqModal] = React.useState(false);

  const [localAutoRecordable, setLocalAutoRecordable] = React.useState(
    Boolean(isAutoRecordable)
  );
  const [localMapToCourse, setLocalMapToCourse] = React.useState(
    Boolean(mapToCourse)
  );
  const [localAccessibility, setLocalAccessibility] = React.useState(
    recordingAccessControl
      ? String(recordingAccessControl)
      : DEFAULT_ACCESSIBILITY_VAL
  );

  React.useEffect(() => {
    setLocalAutoRecordable(Boolean(isAutoRecordable));
  }, [isAutoRecordable]);

  React.useEffect(() => {
    setLocalMapToCourse(Boolean(mapToCourse));
  }, [mapToCourse]);

  React.useEffect(() => {
    if (
      recordingAccessControl !== undefined &&
      recordingAccessControl !== null
    ) {
      setLocalAccessibility(String(recordingAccessControl));
    }
  }, [recordingAccessControl]);

  React.useEffect(() => {
    setHasShareRecordingAccess(
      hasMenuPermission(
        RESOURCE_KEYS.SUBSECTIONS.CREATE_EDIT_LISTINGS.SHARE_ZOOM_RECORDING
      )
    );
  }, []);

  // Evaluates whether session recording toggle is ON (bound to is_auto_recordable)
  const isSessionRecordingOn = form
    ? Boolean(isAutoRecordable)
    : localAutoRecordable;

  const currentMapToCourse = form ? mapToCourse : localMapToCourse;
  const currentAccessibility = form
    ? form.getState()?.values?.[
        SHARE_RECORDING_FORM_KEYS.RECORDING_ACCESSIBILITY
      ] ?? localAccessibility
    : localAccessibility;

  // Disabled flags for switches
  const isSessionRecordingDisabled = !hasShareRecordingAccess;
  const isAutoPackageDisabled =
    !hasShareRecordingAccess || !isSessionRecordingOn;

  // Checked state for Auto-package toggle (bound to map_to_course)
  const isAutoPackageChecked = Boolean(
    isSessionRecordingOn && currentMapToCourse
  );

  const sessionTitle = showAutoPackage
    ? SHARE_RECORDING_TEXTS.SESSION_RECORDING_TITLE
    : SHARE_RECORDING_TEXTS.SHARE_WITH_CUSTOMERS_TITLE;

  const sessionDesc = showAutoPackage
    ? SHARE_RECORDING_TEXTS.SESSION_RECORDING_DESC
    : SHARE_RECORDING_TEXTS.SHARE_WITH_CUSTOMERS_DESC;

  const handleSessionRecordingToggle = React.useCallback(() => {
    const nextVal = !isSessionRecordingOn;
    if (form) {
      form.change(SHARE_RECORDING_FORM_KEYS.IS_AUTO_RECORDABLE, nextVal);
      if (nextVal) {
        form.change(SHARE_RECORDING_FORM_KEYS.MAP_TO_COURSE, true);
      } else {
        form.change(SHARE_RECORDING_FORM_KEYS.MAP_TO_COURSE, false);
      }
    } else {
      setLocalAutoRecordable(nextVal);
      setLocalMapToCourse(nextVal);
    }
  }, [form, isSessionRecordingOn]);

  const handleAutoPackageToggle = React.useCallback(() => {
    if (form) {
      form.change(SHARE_RECORDING_FORM_KEYS.MAP_TO_COURSE, !mapToCourse);
    } else {
      setLocalMapToCourse((prev) => !prev);
    }
  }, [form, mapToCourse]);

  const handleCustomerRecordingAccessChange = React.useCallback(
    (e) => {
      const val = e.target?.value;
      if (form) {
        form.change(SHARE_RECORDING_FORM_KEYS.RECORDING_ACCESSIBILITY, val);
      } else {
        setLocalAccessibility(val);
      }
    },
    [form]
  );

  return {
    hasShareRecordingAccess,
    showFaqModal,
    setShowFaqModal,
    isSessionRecordingOn,
    isSessionRecordingDisabled,
    isAutoPackageDisabled,
    isAutoPackageChecked,
    currentAccessibility,
    localAccessibility,
    sessionTitle,
    sessionDesc,
    handleSessionRecordingToggle,
    handleAutoPackageToggle,
    handleCustomerRecordingAccessChange,
  };
};
